/**
 * Every visible string in the admin app. **English only, on purpose.**
 *
 * The Studio is EN + TR because parents read it; this is an internal tool with a handful of
 * users, all of whom read English, and a Turkish pass here would be maintenance with no
 * reader. It is therefore **not** a message catalog: there is no `en`/`tr` pair, so the
 * Studio's `i18n/parity.test.ts` has nothing to compare and does not apply to this file.
 *
 * The **data** is a different matter. Every `Localized` field an admin edits (a medium's
 * name, tagline, `whatArrives` rows; a global's name and notes) has EN and TR inputs, because
 * that content *is* what a Turkish-speaking parent reads. The chrome around it is English;
 * what it edits is bilingual.
 */

import type { AttentionReason } from '@atlas-nook/mediums';

/** The reason words, and the one place they are named for a reader. */
const ATTENTION_WORDS: Record<AttentionReason, string> = {
  price: 'price',
  shipsIn: 'lead time',
  formatLabel: 'format label',
};

/** “price”, “price and lead time”, “price, lead time and format label”. */
function nameThem(reasons: readonly AttentionReason[]): string {
  const words = reasons.map((reason) => ATTENTION_WORDS[reason]);
  if (words.length < 2) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

export const ADMIN_STRINGS = {
  app: {
    name: 'Atlas Nook',
    descriptor: 'admin',
    mainLabel: 'Admin',
    skipToMain: 'Skip to the main area',
  },

  gate: {
    opening: 'Opening…',
    notAdminTitle: 'This is the admin app, and your account is not an admin.',
    notAdminBody:
      'The catalog and the curated library are edited from here, and that needs the admin claim on your account. Nothing else on this page loads without it.',
    notAdminWho: (email: string) => `Signed in as ${email}.`,
    signOut: 'Sign out',
  },

  signin: {
    title: 'Sign in',
    lead: 'Your email, then a way in: a link we send, a password, or Google.',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    emailRequired: 'An email address, please.',
    send: 'Send me a link',
    sending: 'Sending…',
    usePassword: 'Use a password',
    password: 'Password',
    passwordRequired: 'Your password, please.',
    passwordTooShort: 'At least 8 characters.',
    signIn: 'Sign in',
    create: 'Create an account',
    forgot: 'Forgot it?',
    working: 'One moment…',
    or: 'or',
    google: 'Continue with Google',
    sentTitle: 'Check your email.',
    sentBody: (email: string) => `A sign-in link is on its way to ${email}.`,
    sentAgain: 'Use a different address',
    resetTitle: 'Check your email.',
    resetBody: (email: string) =>
      `A password reset link is on its way to ${email}. If there is no account for that address, nothing will arrive.`,
    back: 'Back',
    emulator: (url: string) => `On the emulator the link is at ${url}.`,
    confirmEmailTitle: 'One more thing.',
    confirmEmailBody:
      'This link was opened somewhere other than where it was asked for. Confirm the address it was sent to.',
    confirmEmailAction: 'Finish signing in',
    finishing: 'Signing you in…',
    offerPassword: 'Use your password instead.',
    offerLink: 'Send me a link instead.',
    failed: 'That did not work. Try again.',
    // the error matrix — the Studio's, in this app's plainer voice
    errBadCredentials:
      'That email and password do not match — or there is no account yet. Try again, reset it, or create one.',
    errEmailInUse:
      'There is already an account for that email. Sign in with its password, or reset it.',
    errWeakPassword: 'A longer password, please — at least 8 characters.',
    errTooMany: 'Too many tries. Wait a moment, then try again.',
    errNetwork: 'Could not reach the sign-in service. Check the connection and try again.',
    errInvalidEmail: 'That does not look like an email address.',
    errPopup: 'The Google window was closed or blocked. Try again.',
    errMethodOff: 'That way in is not switched on. Try another.',
    errRecentLogin: 'Sign in again first, then try once more.',
    errAccountExistsLink: 'That address already comes in with an email link.',
    errAccountExistsPassword: 'That address already comes in with a password.',
  },

  account: {
    methods: (methods: string) => `Signs in with ${methods}`,
    methodLink: 'email link',
    methodPassword: 'password',
    methodGoogle: 'Google',
    methodEmail: 'email',
    verifyNote: 'Verify your email — we sent a link.',
    verifyConsequence:
      'Until you do, signing in with Google for this address will replace your password.',
    verifyResend: 'Send it again',
    verifyDismiss: 'Not now',
    verifySent: 'Verification email sent.',
    changePassword: 'Change password',
    setPassword: 'Set a password',
  },

  password: {
    changeTitle: 'Change your password',
    setTitle: 'Set a password',
    setBody: 'A password beside the way you already come in. Your link and Google keep working.',
    current: 'Current password',
    next: 'New password',
    nextHelp: 'At least 8 characters.',
    required: 'Your current password, please.',
    confirm: 'Save',
    cancel: 'Cancel',
    working: 'Saving…',
    changed: 'Password changed.',
    set: 'Password set.',
    errCurrent: 'That is not your current password.',
    errRecentLogin:
      'Firebase wants a fresh sign-in for this. Sign out, come back in with a new link, and try again.',
  },

  nav: {
    sections: 'Admin sections',
    catalog: 'Catalog',
    globals: 'Global library',
    issues: 'Issues',
    account: (email: string) => `Account · ${email}`,
    database: (name: string) => `database ${name}`,
    signOut: 'Sign out',
  },

  common: {
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved.',
    savedWithProblems: (count: number) =>
      `Saved. ${count} problem${count === 1 ? '' : 's'} still to fix.`,
    cancel: 'Cancel',
    close: 'Close',
    create: 'Create',
    add: 'Add',
    remove: 'Remove',
    en: 'English',
    tr: 'Turkish',
    none: '—',
    notSet: 'not set',
    yes: 'Yes',
    no: 'No',
    back: 'Back',
    failed: 'That write did not go through.',
  },

  catalog: {
    kicker: 'Catalog',
    title: 'Mediums',
    meta: (total: number, published: number, needsAttention: number) =>
      `${total} rows · ${published} published` +
      (needsAttention > 0 ? ` · ${needsAttention} need attention` : ''),
    tableLabel: 'The mediums catalog, in publication order',
    reorderLabel: (name: string) => `Reorder — ${name}. Drag, or use arrow up and arrow down.`,
    empty: 'The catalog is empty. Seed it, or create the first row.',
    newMedium: 'New medium',
    columns: {
      order: 'Order',
      format: 'Format',
      name: 'Name',
      tier: 'Tier',
      layout: 'Layout',
      availability: 'Availability',
      state: 'State',
    },
    state: {
      published: 'Published',
      comingSoon: 'Coming soon',
      hidden: 'Hidden',
      /** A published row the publish gate would refuse today. Names what it is missing. */
      needs: (reasons: readonly AttentionReason[]) => `Published · needs ${nameThem(reasons)}`,
    },
    needsAttention: 'Needs attention',
    tierValue: (tier: number | null) => (tier === null ? 'ruled out' : `tier ${tier}`),
    open: 'Open',
    newTitle: 'New medium',
    newBody:
      'The slug is the document id and cannot be changed afterwards. Everything else is editable on the row.',
    slug: 'Slug',
    slugHelp: 'Lowercase, hyphen-separated: kamishibai, accordion-book.',
    slugInvalid: 'A slug is lowercase letters, digits and single hyphens.',
    slugTaken: 'That slug is already in the catalog.',
  },

  medium: {
    kicker: 'Medium',
    notFound: 'No medium with that slug.',
    slugLocked: (id: string) => `Slug ${id} · locked after create`,
    problemsTitle: 'This row has problems, and they are the reader’s problems too:',
    sections: {
      copy: 'Copy',
      shelf: 'The shelf',
      audience: 'Audience',
      decision: 'Decision support',
      generation: 'Generation',
      composition: 'Composition',
      illustration: 'Illustration',
      arrives: 'What arrives',
      commerce: 'Reserved commerce fields',
      record: 'Record',
    },

    name: 'Name',
    family: 'Family',
    formatLabel: 'Format label',
    formatLabelHelp: 'The chip above the headline name in the store — “Accordion”, “Kamishibai”.',
    tagline: 'Tagline',
    description: 'Description',
    physicalAction: 'Physical action',
    physicalActionHelp: 'The verb. What the child’s hands do — “slide the scenery”.',
    storytellingQuality: 'Storytelling quality',

    availability: 'Availability',
    order: 'Order',
    orderHelp: 'The catalog’s own sort. Dragging a row on the list writes this.',
    featured: 'Featured',

    ageMin: 'Age from',
    ageMax: 'Age to',
    youngSafe: 'Young-safe',
    youngSafeHelp: 'Small parts, lamps, mechanisms — is it safe at the young end of the band?',

    tier: 'Tier',
    // `engine/09` q4: tier 0 stays admin discipline — the hint says what it means, and no
    // validator enforces it.
    tierHelp: '0 · the base — the printed book only',
    productionEase: 'Production ease',
    cutterDependency: 'Cutter dependency',
    consistencyRisk: 'Consistency risk',
    refillModel: 'Refill model',
    narrativeStrength: 'Narrative strength',
    ruledOut: 'Ruled out as a shipping format',
    ruledOutReason: 'Why it is ruled out',
    ruledOutHelp: 'A ruled-out row keeps its place in the catalog, tagged with the honest reason.',

    frameModel: 'Frame model',
    generationNotes: 'Generation notes',

    hasComposition: 'This medium renders to a physical or video output',
    hasCompositionHelp:
      'The five digital platform plays carry no composition — they never render. Film is the one digital medium that does.',
    layout: 'Layout archetype',
    input: 'Scene artifact',
    render: 'Render mode',
    scenesMin: 'Scenes · min',
    scenesIdeal: 'Scenes · ideal',
    scenesMax: 'Scenes · max',
    selectStrategy: 'Select strategy',
    pieces: 'Cut pieces',
    piecesHelp: 'Layer roles that become separate cut pieces. Only meaningful with layers.',
    outputs: 'Outputs',

    illustration: 'Illustration',
    illustrationHelp:
      'A photograph of the made object. Public: the store renders it at build time and the Studio’s picker shows it. PNG, JPEG or WebP, up to 5 MB.',
    illustrationNone: 'No illustration yet',
    illustrationAdd: 'Upload an illustration',
    illustrationReplace: 'Replace it',
    illustrationRemove: 'Forget it',
    illustrationChoose: 'Choose an illustration file',
    illustrationWrongType: 'PNG, JPEG or WebP.',
    illustrationTooLarge: 'That file is over 5 MB.',
    illustrationFailed: 'The upload did not go through.',
    illustrationPath: (path: string) => `Stored at ${path}`,

    arrivesHelp:
      'The “what arrives in the box” rows the keepsake page reads. Facts, in the store’s voice — never price, stock or shipping.',
    arrivesAdd: 'Add a row',
    arrivesRemove: (index: number) => `Remove row ${index}`,

    price: 'Price',
    currency: 'Currency',
    shipsIn: 'Ships in',
    checkoutUrl: 'Checkout link',
    publishNeeds: (missing: string) => `Publishing needs ${missing}`,
    baseSku: 'Base SKU',
    refillSku: 'Refill SKU',
    commerceHelp:
      'Published means sold: a published keepsake needs a price and a lead time, and they are what the store and the Studio display. The provider charges — the checkout link is the only way there, and the SKUs below stay reserved for a base + refill structure (PRD 03 §4.4).',

    // ── photographs (PRD 03 §5) ──
    photographs: 'Photographs',
    photosHelp:
      'The answer to “does this exist?” is a photograph of the object, not a quote from anyone. Plain surface, daylight, no people — a pair of hands at most. Measure it, and use the measurement. No child’s name visible on any page.',
    photosNone:
      'No photographs yet. The keepsake page shows its rendering alone, which is the honest state.',
    photoAddStill: 'Add a photograph',
    photoAddClip: 'Add a clip',
    photoStill: 'Photograph',
    photoClip: 'Clip',
    photoCaption: 'Caption',
    photoCaptionHelp: 'One factual sentence about the object. Never how anyone felt about it.',
    photoSize: 'Measured size',
    photoSizeHelp:
      'As you measured it — “22 × 14 cm folded”. Leave it empty and the caption says the size is not final.',
    photoOnHome: 'On the home row',
    photoOnHomeHelp:
      'The home shows the first three ticked, across published mediums, in shelf order.',
    photoFile: (path: string) => `Stored at ${path}`,
    photoPoster: 'Poster still',
    photoPosterMissing:
      'A clip needs a poster still, or it is a black rectangle until someone presses it.',
    photoReplaceFile: 'Replace the file',
    photoChooseStill: 'Choose a photograph file',
    photoChooseClip: 'Choose a clip file',
    photoChoosePoster: 'Choose a poster still',
    photoRemove: (index: number) => `Remove photograph ${index}`,
    photoUp: (index: number) => `Move photograph ${index} earlier`,
    photoDown: (index: number) => `Move photograph ${index} later`,
    photoWrongTypeStill: 'A photograph is a PNG, JPEG or WebP.',
    photoWrongTypeClip: 'A clip is an MP4 or a WebM.',
    photoTooLargeStill: 'That file is over 5 MB.',
    photoTooLargeClip: 'A clip has to be under 2 MB. Six silent seconds of paper is far less.',
    photoFailed: 'The upload did not go through.',
    photoSaved: 'Photographs saved.',

    createdBy: (who: string) => `Created by ${who}`,

    publishState: 'Shelf state',
    publish: 'Publish',
    unpublish: 'Unpublish',
    comingSoon: 'Coming soon',
    notComingSoon: 'Stop teasing it',
    publishTitle: (name: string) => `Publish ${name}?`,
    publishBody:
      'Published mediums appear in the Studio’s picker immediately. The store needs a rebuild before it shows.',
    publishConfirm: 'Publish it',
    unpublishTitle: (name: string) => `Unpublish ${name}?`,
    unpublishBody:
      'It disappears from the Studio’s picker immediately — a parent part-way through making one loses the option. The store needs a rebuild before it stops showing.',
    unpublishConfirm: 'Unpublish it',
    comingSoonTitle: (name: string) => `Tease ${name} as coming soon?`,
    comingSoonBody:
      'It appears on the store as coming soon, and stays out of the Studio’s picker. The store needs a rebuild.',
    comingSoonConfirm: 'Tease it',
    notComingSoonTitle: (name: string) => `Stop teasing ${name}?`,
    notComingSoonBody:
      'It goes back to being invisible outside this app. The store needs a rebuild.',
    notComingSoonConfirm: 'Stop teasing it',
    blockedByProblems: 'Fix the problems above before publishing it.',
    /** Already on the shelf, and short of what the shelf displays. */
    onShelfNeeds: (reasons: readonly AttentionReason[]) =>
      `On the shelf without ${nameThem(reasons)} — the store shows the gap, or worse, hides it.`,
  },

  globals: {
    kicker: 'Global library',
    title: 'Curated pieces',
    meta: (active: number, archived: number) => `${active} on the shelf · ${archived} archived`,
    listLabel: 'The curated global library',
    empty: 'Nothing curated yet.',
    newGlobal: 'New global',
    newTitle: 'New global piece',
    newBody:
      'A global primitive is a curator publication: owned by the system, world-readable, and writable only from here. Families fork it; they never edit it.',
    kind: 'Kind',
    nameEn: 'Name · English',
    nameTr: 'Name · Turkish',
    nameRequired: 'A name, please.',
    archivedTag: 'Archived',
    notDrawn: 'Not drawn yet',
    drawn: 'Drawn',
    showArchived: 'Archived',
    filterLabel: 'Filter the library',
  },

  global: {
    kicker: 'Global piece',
    notFound: 'No global piece with that id.',
    identity: 'Identity',
    identityHelp:
      'Identity edits bump the version, which re-addresses the base reference and asks for approval again. Renames, descriptions and axes do not.',
    description: 'Description',
    identityNotes: 'Identity notes',
    identityNotesHelp: 'What this piece IS. It goes into every prompt drawn from it.',
    colourAnchor: 'Colour anchor',
    colourAnchorHelp: 'Colours that must not drift — “rust coat, cream chest”.',
    version: (version: number) => `version ${version}`,
    bumpTitle: 'This changes who they are.',
    bumpBody:
      'Identity edits bump the version. The reference already drawn stays where it is, but this piece points at a new address and has to be drawn and approved again.',
    bumpConfirm: 'Save and draw it again',
    bumpsVersion: 'this bumps the version',
    bumped: (version: number) => `Saved. Now at version ${version}.`,
    savedNoBump: 'Saved. The version did not move.',

    axes: 'Ways to vary it',
    axesNone: 'No axes yet. Add one to describe how this piece varies.',
    axesHint: 'Axes are not identity: editing them never re-opens the approval.',
    axesBound: 'At most 4 axes, 8 options each, 256 points in all.',
    axesTooBig: 'That would describe more variants than the guardrail allows.',
    addAxis: 'Add a way to vary it',
    newAxis: 'What varies',
    addOption: 'Add an option',
    newOption: (axis: string) => `New ${axis} option`,
    removeOption: (axis: string, option: string) => `Remove ${option} from ${axis}`,
    removeAxis: (axis: string) => `Remove the ${axis} axis`,
    optionsCount: (count: number) => `${count} option${count === 1 ? '' : 's'}`,

    archive: 'Archive',
    restore: 'Restore',
    archiveTitle: (name: string) => `Archive ${name}?`,
    archiveBody:
      'It leaves the shared shelf. Anything already using it keeps resolving and keeps rendering — this is the take-down, and it is reversible.',
    archiveConfirm: 'Archive it',
    archived: 'Archived.',
    restored: 'Restored.',
    noDelete:
      'There is no permanent delete for a global. A curated publication outlives whoever filed it; archiving is the take-down.',

    reference: 'Base reference',
    referenceNone: 'Nothing drawn yet. Draw it and the shelf can use it.',
    draw: 'Draw base reference',
    drawAgain: 'Draw it again',
    drawTitle: (name: string) => `Draw ${name}?`,
    drawBody:
      'This asks the pipeline for one image. The record carries the global path and no requester — it belongs to nobody, which is what makes it the shared shelf’s.',
    drawConfirm: 'Draw it',
    redrawTitle: (name: string) => `Draw ${name} again?`,
    redrawBody:
      'The picture already there is replaced at the same address. It costs another generation.',
    redrawConfirm: 'Draw it again',
    making: 'Making',
    failed: 'It didn’t come back',
    retry: 'Try again',
    retryFailed: 'That could not be reopened.',
    sheet: 'Model sheet',
    sheetHelp:
      'A character is established from a sheet. Approving it runs the free split, which cuts the sheet into starter variants and re-points the reference at the canonical crop.',
    approve: 'Approve this reference',
    approveTitle: (name: string) => `Approve ${name}’s reference?`,
    approveBody:
      'This locks the identity for the shared shelf and runs the split. Every variant afterwards is drawn from it.',
    approveConfirm: 'Approve it',
    approved: 'Approved. The split is running.',
    approvedTag: 'Approved · locked',
    awaitingApproval: 'Drawn, not approved',
    readyTag: 'Ready',
    sheetSplit: (usable: number, expected: number) => `${usable} of ${expected} cells usable`,
    remakeRecommended: 'The sheet came back thin. A remake is worth it.',

    matrix: 'Variants',
    matrixHint:
      'Pre-render a few points so a starter arrives with more than one pose. Each one is a paid image.',
    matrixBlocked: 'Nothing else is drawn until the reference is approved.',
    matrixLabel: (name: string, columns: string) => `${name} variants, by ${columns}`,
    matrixLabelTwo: (name: string, columns: string, rows: string) =>
      `${name} variants, ${columns} across and ${rows} down`,
    cellOpen: (name: string, point: string) => `${name}, ${point} — not drawn. Draw it.`,
    cellBlocked: (name: string, point: string) =>
      `${name}, ${point} — the reference is not approved yet.`,
    cellFilled: (name: string, point: string) => `${name}, ${point} — drawn.`,
    cellMaking: (name: string, point: string) => `${name}, ${point} — being drawn.`,
    cellError: (name: string, point: string) => `${name}, ${point} — it didn’t come back.`,
    tapToDraw: 'draw it',
    tryAgain: 'try again',
    heldAxis: (axis: string) => `Held ${axis}`,
    madeOpen: (made: number, open: number) => `${made} drawn · ${open} open`,
  },

  issues: {
    kicker: 'Issues',
    title: 'What the flows turned up',
    meta: (total: number, open: number) => `${total} filed · ${open} open`,
    tableLabel: 'Filed issues, newest first',
    empty: 'Nothing filed. Walk a flow and write down what surprises you.',
    newIssue: 'New issue',
    filterStatus: 'Filter by status',
    filterArea: 'Filter by area',
    noneMatch: 'Nothing matches those filters.',
    columns: {
      id: 'Id',
      area: 'Area',
      kind: 'Kind',
      step: 'Step',
      title: 'What happened',
      status: 'Status',
    },
    status: {
      open: 'Open',
      fixing: 'Fixing',
      fixed: 'Fixed',
      verified: 'Verified',
      wontfix: 'Won’t fix',
    },
    kind: {
      bug: 'Bug',
      rough: 'Rough',
      idea: 'Idea',
    },
    area: {
      studio: 'Studio',
      admin: 'Admin',
      patara: 'Patara',
      engine: 'Engine',
      devops: 'Devops',
      design: 'Design',
      docs: 'Docs',
    },
    copyPrompt: 'Copy fix prompt',
    copyPromptThorough: 'Copy fix prompt · thorough',
    copied: 'Copied',
    copyPromptFor: (id: string) => `Copy fix prompt for ${id}`,
    copyPromptThoroughFor: (id: string) => `Copy fix prompt · thorough for ${id}`,
    copyAllOpen: 'Copy fix prompt for all open',
    copyAllOpenThorough: 'Copy fix prompt · thorough for all open',
    copyAllOpenLabel: (count: number) =>
      `Copy fix prompt for all ${count} open issue${count === 1 ? '' : 's'}`,
    copyAllOpenThoroughLabel: (count: number) =>
      `Copy fix prompt · thorough for all ${count} open issue${count === 1 ? '' : 's'}`,
    copyFallbackTitle: 'Copy it by hand',
    copyFallbackBody:
      'This browser will not let the page reach the clipboard — an insecure context, or a refused permission. The prompt is selected below; copy it and paste it into Claude Code.',
    copyFallbackLabel: 'The fix prompt, ready to copy',
    columnCopy: 'Prompt',

    newTitle: 'File an issue',
    newBody:
      'One line on what happened, where it was, and which step of FLOWS.md you were on. A screenshot if it helps.',
    titleField: 'What happened',
    titleHelp: 'One line. The exact words on screen beat a description.',
    titleRequired: 'A line, please.',
    areaField: 'Area',
    kindField: 'Kind',
    kindHelp: 'Bug: it’s wrong. Rough: it works but reads wrong. Idea: a wish.',
    stepField: 'Step',
    stepHelp: 'The FLOWS.md reference — F4.3, S8. Leave it empty if you were outside a flow.',
    noteField: 'Anything else',
    noteHelp: 'Free text. Line breaks are kept; nothing is rendered as markdown.',
    shots: 'Screenshots',
    shotsChoose: 'Choose screenshots',
    shotsAdd: 'Add screenshots',
    shotsStaged: (count: number) => `${count} file${count === 1 ? '' : 's'} ready to upload`,
    shotsWrongType: 'PNG, JPEG or WebP.',
    shotsTooLarge: 'That file is over 10 MB.',
    shotsFailed: 'The upload did not go through — the issue was filed without it.',
    file: 'File it',
  },

  issue: {
    kicker: 'Issue',
    notFound: 'No issue with that id.',
    meta: (id: string, area: string, kind: string) => `${id} · ${area} · ${kind}`,
    step: (step: string) => `step ${step}`,
    filed: (when: string) => `Filed ${when}`,
    note: 'The note',
    noNote: 'No note beyond the line above.',
    shots: 'Screenshots',
    shotsNone: 'No screenshots.',
    shotPath: (path: string) => path,
    log: 'The log',
    logEmpty: 'Nothing said yet.',
    logBy: (who: string, when: string) => `${who} · ${when}`,
    voice: { namik: 'Namık', agent: 'the agent' },
    say: 'Say something',
    sayHelp: 'Setting a status with no word on it is what makes a tracker useless six weeks later.',
    statusField: 'Status',
    reply: 'Reply',
    post: 'Post it',
    posted: 'Posted.',
    nothingToPost: 'Change the status, write a reply, or both.',
    cliHint: (id: string) => `The agent reads this as: issues show ${id}`,
  },

  cost: {
    oneGeneration: (usd: string) => `One generation · about ${usd}`,
    ours: 'Our money, not a family’s.',
  },
} as const;
